import React, {
  useState, useContext, useRef, useEffect, useMemo, forwardRef, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { LeadContext } from 'Leads/Details/hooks/context';
import Report from 'Leads/Details/Proposals/Report';
import { ProductionRunQueue } from 'Leads/Details/DesignTool/production-selection';
import {
  CURRENT_PROPOSAL_VERSION,
  LEGACY_PROPOSAL_VERSION,
  postToParent,
} from './postMessage';
import { resolveNavigationDesignId } from './commands';

import {
  assert, string, object, number, nonempty, integer, optional, boolean, max, enums, define, size, min, defaulted, array, nullable, union, pattern,
} from 'superstruct';

const IFrameShadingReportScheme = optional(object({
  homeowner: optional(string()),
  client: optional(string()),
  address: optional(string()),
  coordinates: optional(pattern(nonempty(string()), /^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/)),
  format: optional(string()),
}));

const Proposal = forwardRef(({ visible, pid, callback, context, companyName }, ref) => {
  const {
    uid,
    user,
    lead,
    proposalAPI,
    hardwareAPI,
    validUtility,
    PVWattsAPI,
    PVWattsHourlyAPI,
    StringingAPI,
    solarProductionOptimalAPI,
    solarProductionSimulateAPI,
  } = useContext(LeadContext);

  const [hardware, setHardware] = useState(null);

  const proposal = useRef();
  const productionRunQueue = useRef(new ProductionRunQueue());
  const history = useHistory();

  const getProposalVersion = () =>
    proposal.current?.getProposalVersion?.() || LEGACY_PROPOSAL_VERSION;

  const proposalCallback = (callbackType, data, versionOverride) => {
    const version = versionOverride || getProposalVersion();
    postToParent(callbackType, data, '*', version);
    if (callback) callback(callbackType, data, version);
  };

  const getNavigationDesignId = (targetUid, source = {}) =>
    resolveNavigationDesignId({
      source,
      search: window.location.search,
      currentUid: uid,
      targetUid,
    });

  const getAccessKeyFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('access-key') || null;
  };

  const getAccessKey = (source = {}) =>
    source?.['access-key'] ||
    source?.accessKey ||
    source?.access_key ||
    source?.apiKey ||
    source?.api_key ||
    getAccessKeyFromUrl();

  const loadProposal = (id, proposalId, options = {}) => {
    const params = new URLSearchParams();
    params.set('id', proposalId);
    const designId = getNavigationDesignId(id, options);
    if (designId) params.set('design_id', designId);
    const accessKey = getAccessKey(options);
    if (accessKey) params.set('access-key', accessKey);
    const newUrl = `/iframe/${id}?${params.toString()}`;
    proposalCallback('Navigation', newUrl, CURRENT_PROPOSAL_VERSION);
    window.location.replace(newUrl);
    // use this instead to eliminate reload after create proposal
    // history.push(`/iframe/${id}?id=${proposalId}`);
  };

  const handleMessage = async (event) => {
    const { cmd, params = null, config = null } = event.data;
    switch (cmd) {
      case 'proposal.LoadProposal':
        if (params) {
          loadProposal(params.uid, params.pid, {
            ...(config || {}),
            ...params,
          });
        }
        break;
      case 'proposal.2D':
        if (proposal.current) proposal.current.switchView('2D');
        break;
      case 'proposal.3D':
        if (proposal.current) proposal.current.switchView('3D');
        break;
      case 'proposal.3DJson':
        if (proposal.current) proposal.current.handleDownload3DJSON();
        break;
      case 'proposal.ToggleShadingOverlay':
        if (proposal.current) proposal.current.toggleOverlay();
        break;
      case 'proposal.TogglePanelSelection':
        if (proposal.current) proposal.current.togglePanelSelection();
        break;
      case 'proposal.TogglePanelVisibility':
        if (proposal.current) proposal.current.toggleVisibility();
        break;
      case 'proposal.ShadingValues':
        if (proposal.current) proposal.current.shadingValues();
        break;
      case 'proposal.RoofOrientation':
        if (proposal.current) proposal.current.pitchAndAzimuith();
        break;
      case 'proposal.ProductionPerPanel':
        if (proposal.current) proposal.current.productionPerPanel();
        break;
      case 'proposal.CalculateProduction':
      case 'proposal.calculateProduction':
      case 'designer.CalculateProduction':
      case 'designer.calculateProduction':
        if (proposal.current?.calculateProduction) {
          await productionRunQueue.current.run(() =>
            proposal.current.calculateProduction({
              ...(config && typeof config === 'object' ? config : {}),
              ...(params && typeof params === 'object' ? params : {}),
            })
          );
        } else {
          proposalCallback('Error', {
            code: '50',
            message:
              'Proposal tool is not available for production calculation.',
          });
        }
        break;
      case 'proposal.ShadingValuesPerArray':
        if (proposal.current) proposal.current.shadingValuesPerArray();
        break;
      case 'proposal.PanelOrientationPerArray':
        if (proposal.current) proposal.current.panelOrientation();
        break;
      case 'proposal.ToggleAllPanels':
        if (proposal.current) proposal.current.toggleAllPanels();
        break;
      case 'proposal.ToggleViewerMode':
        if (proposal.current) proposal.current.toggleViewerMode();
        break;
      case 'proposal.ProposalCosts':
        if (proposal.current) proposal.current.calcCosts();
        break;
      case 'proposal.Save':
        if (proposal.current) proposal.current.updateReport();
        break;
      case 'proposal.CaptureImage':
        if (proposal.current) {
          proposal.current.captureImage();
        }
        break;
      case 'proposal.CaptureThumbnail':
        if (proposal.current) {
          proposal.current.captureThumbnail();
        }
        break;
      case 'proposal.Copy':
        if (proposal.current) {
          if (params && params.pid) {
            proposal.current.copyProposal(params.pid);
          } else {
            proposal.current.copyProposal();
          }
        }
        break;
      case 'proposal.ShadingReport':
        if (proposal.current) {
          if (params) {
            assert(params, IFrameShadingReportScheme);
            console.log(`shading report with params: \n${params}`);
            proposal.current.downloadShadingReport(params);
          } else {
            console.log('shading report without params')
            proposal.current.downloadShadingReport();
          }
        }
        break;
      case 'proposal.SetOffset':
        if (proposal.current) {
          if (params) proposal.current.solarCalculationsWithOffset(params.offset);
        }
        break;
      case 'proposal.switchAnnotationMode':
        if (proposal.current) proposal.current.switchAnnotationMode();
        break;
      case 'proposal.setAnnotations':
        if (proposal.current) proposal.current.getAnnotations(params);
        break;
      case 'proposal.removeAnnotation':
        if (proposal.current && (params?.index || params?.index >=0)) proposal.current.removeAnnotation(params.index);
        break;
      case 'proposal.getAnnotations':
        if (proposal.current) proposal.current.getAnnotations();
        break;
      case 'proposal.autoString':
        if (proposal.current) {
          if (params) proposal.current.autoString(params.type);
          else proposal.current.autoString();
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    hardwareAPI.list().then((data) => { setHardware(data); });
  }, []);

  useImperativeHandle(ref, () => ({
    handleMessage,
    loadProposal,
    proposalCallback,
    uid,
    lead,
    proposal,
  }));

  const iframeProposal = useMemo(() => {
    if (!lead) return null;
    const fullAddress = `${lead.property.street}, ${lead.property.city}, ${lead.property.state}`;
    return (
      <Report
        ref={proposal}
        pid={pid}
        API={proposalAPI}
        PVWattsAPI={PVWattsAPI}
        PVWattsHourlyAPI={PVWattsHourlyAPI}
        StringingAPI={StringingAPI}
        solarProductionOptimalAPI={solarProductionOptimalAPI}
        solarProductionSimulateAPI={solarProductionSimulateAPI}
        hardware={hardware}
        currentUser={user}
        fullAddress={fullAddress}
        lead={lead}
        validUtility={validUtility}
        callback={proposalCallback}
        context={context}
        iframe
        companyName={companyName}
      />
    );
  }, [uid, lead, pid, hardware, context]);

  if (!visible || !hardware) return null;
  if (hardware) return iframeProposal;
});

Proposal.propTypes = {
  // pid: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
};

export default Proposal;
